import { normalizeReportComponent } from '@erp/shared-types';
import type { ComponentRegistry } from './registry';
import type { GridLayout, ReportComponentConfig } from './types';

// ── Persisted-layout hydration ────────────────────────────────────────────────
// Layouts are stored as JSON (org defaults + per-user overrides) and outlive
// code changes. Hydration reconciles a persisted layout with the current
// registry on read:
//  - unknown component types pass through verbatim (rendered as an
//    "unavailable" placeholder; their config survives the next save),
//  - known types run their registry migration steps from the persisted
//    version to the current one,
//  - missing fields are filled from the type's `make()` defaults, which makes
//    additive config changes free (no version bump needed).
// Saves persist the hydrated layout, so stored configs converge to the current
// version whenever a user next saves.

function hydrateComponent(
  component: ReportComponentConfig,
  registry: ComponentRegistry,
): ReportComponentConfig {
  const def = registry[component.type];
  if (!def) return component;

  let config: Record<string, unknown> = {
    ...normalizeReportComponent(component),
  };
  for (let v = component.v ?? 1; v < def.version; v++) {
    const step = def.migrations?.[v];
    if (step) config = step(config);
  }

  // Defaults-fill: persisted keys win over defaults; the stamped `v` marks the
  // config as current so hydration is idempotent.
  return {
    ...def.make(component.id),
    ...config,
    type: component.type,
    id: component.id,
    v: def.version,
  };
}

export function hydrateLayout(
  layout: GridLayout,
  registry: ComponentRegistry,
): GridLayout {
  return layout.map((item) => ({
    ...item,
    component: hydrateComponent(item.component, registry),
  }));
}
